export const MARKING_TYPES = [
  'multiple_choice',
  'numeric',
  'algebraic',
  'exact_text',
  'unit',
  'sig_fig',
  'ai',
  'hybrid',
  'manual',
] as const;

export type MarkingType = (typeof MARKING_TYPES)[number];

const MARKING_TYPE_SET = new Set<string>(MARKING_TYPES);

export type MarkingTypeSource = {
  type?: string | null;
  answer?: string | null;
  answer_index?: number | null;
  marking_method?: string | null;
  deterministic_config?: unknown;
  worked_solution?: string | null;
};

const hasText = (value?: string | null) => typeof value === 'string' && value.trim().length > 0;

const configMarkingType = (config: unknown): MarkingType | null => {
  if (!config || typeof config !== 'object' || Array.isArray(config)) return null;
  const type = (config as { type?: unknown }).type;
  if (typeof type !== 'string' || type === 'auto' || !MARKING_TYPE_SET.has(type)) return null;
  return type as MarkingType;
};

/** Use the stored marking type, or pick one from MARKING_TYPES when it is missing/`auto`. */
export const resolveMarkingType = (question: MarkingTypeSource): MarkingType => {
  const stored = String(question.marking_method ?? '').trim();
  if (stored && stored !== 'auto' && MARKING_TYPE_SET.has(stored)) {
    return stored as MarkingType;
  }

  const fromConfig = configMarkingType(question.deterministic_config);
  if (fromConfig) return fromConfig;

  const type = String(question.type ?? '');
  if (type === 'mc' || type === 'table' || question.answer_index != null) {
    return 'multiple_choice';
  }
  if (hasText(question.answer)) return 'numeric';
  if (hasText(question.worked_solution)) return 'ai';
  return 'manual';
};

export const isDeterministicMarkingType = (type: MarkingType): boolean =>
  type === 'numeric' ||
  type === 'algebraic' ||
  type === 'exact_text' ||
  type === 'unit' ||
  type === 'sig_fig';

export const isAiMarkingType = (type: MarkingType): boolean => type === 'ai' || type === 'hybrid';

export const isManualMarkingType = (type: MarkingType): boolean => type === 'manual';

export const clientScoresMarkingType = (type: MarkingType): boolean =>
  type === 'multiple_choice' || isDeterministicMarkingType(type);
